import {invalidateTickets} from "@/api/reviewTicketsApi";
import {client} from "@/httpClient";
import {useMutation, type UseMutationOptions} from "@tanstack/react-query";
import dayjs from "dayjs";

export const CAMPAIGN_APPLICATION_KEYS = {
  mApplication: (applicationId: number) => [`/campaign_application/${applicationId}/`],
  mApplicationAccept: (applicationId: number) => [`/campaign_application/${applicationId}/accept/`],
  mApplicationAddContentByUrl: (applicationId: number) => [
    `/campaign_application/${applicationId}/add_content_by_url/`,
  ],
  mApplicationCancel: (applicationId: number) => [`/campaign_application/${applicationId}/cancel/`],
  mApplicationDecline: (applicationId: number) => [`/campaign_application/${applicationId}/decline/`],
  mApplicationExtendCompleted: (applicationId: number) => [`/campaign_application/${applicationId}/extend_completed/`],
  mApplicationFeaturedInstagramMedia: ["/campaign_application/feature_instagram_media/"],
  mApplicationFeaturedTikTokMedia: ["/campaign_application/feature_tiktok_media/"],
  mApplicationFeaturedUgcMedia: (contentId: number) => [`/ugc_media/${contentId}/feature/`],
  mApplicationLikedMedia: ["/campaign_application/like_media/"],
  mApplicationPending: (applicationId: number) => [`/campaign_application/${applicationId}/pending/`],
  mApplicationRefund: (applicationId: number) => [`/campaign_application/${applicationId}/refund_cash_reward/`],
  mApplicationRenegotiation: (applicationId: number) => [`/campaign_application/${applicationId}/renegotiate_price/`],
  mApplicationSpeedyReward: (applicationId: number) => [`/campaign_application/${applicationId}/payout_speedy_reward/`],
  mApplicationUpdateTracking: (applicationId: number) => [`/campaign_application/${applicationId}/update_tracking/`],
  qApplicationSourceMaterials: (applicationId: number) => [`/campaign_application/${applicationId}/source_materials/`],
} as const;

export const CAMPAIGN_REQUEST_KEYS = {
  mAcceptCampaignRequest: (requestId: number) => [`/campaign_requests/${requestId}/accept/`],
  mBrandCancellationRequest: (applicationId: number): [string, number] => [`/campaign_requests/`, applicationId],
  mDeclineCampaignRequest: (requestId: number) => [`/campaign_requests/${requestId}/decline/`],
} as const;

export const useAddContentByUrlMutation = ({
  applicationId,
  mutationOptions,
}: {
  applicationId: number;
  mutationOptions?: Partial<
    UseMutationOptions<unknown, Error, {url: string; skip_content_approval: boolean; video_code?: string}>
  >;
}) =>
  useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationAddContentByUrl(applicationId),
    mutationFn: (data: {url: string; skip_content_approval: boolean; video_code?: string}) =>
      client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationAddContentByUrl(applicationId)[0], data),
    ...mutationOptions,
  });

export const useMarkTaskCompletedMutation = () => {
  // Reuses the existing campaign-application update endpoint that the admin tools menu
  // already uses to mark a task complete -- sets the manual-completion flag, stamps the
  // completion timestamp, and clears the unreliable flag, so the creator is credited the
  // same way as a manual completion elsewhere in the app.
  return useMutation({
    mutationFn: async ({applicationId}: {applicationId: number}) => {
      const response = await client.patch(CAMPAIGN_APPLICATION_KEYS.mApplication(applicationId)[0], {
        manual_task_completed: true,
        task_completed_on: dayjs().toISOString(),
        unreliable: false,
      });
      return response.data;
    },
    onSuccess: invalidateTickets,
  });
};

export const usePayoutSpeedyRewardMutation = ({
  applicationId,
  mutationOptions,
}: {
  applicationId: number;
  mutationOptions?: Partial<UseMutationOptions>;
}) =>
  useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationSpeedyReward(applicationId),
    mutationFn: () => client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationSpeedyReward(applicationId)[0]),
    ...mutationOptions,
  });
